// ── SpecExecutionOutcome ──
// First-class, immutable outcome record produced when a JobSpec-driven job
// reaches terminal QA PASS. This is the foundation of the outcome dataset:
// every analytics, scoring, or optimization layer that ever ships should
// read from this shape — never re-derive from mutable execution state.
//
// Design constraints (locked):
//  • Pure derivation only. No side effects in `deriveOutcome`.
//  • Idempotent: rerunning on the same inputs yields an equivalent record.
//  • Tied to the pinned BoundJobSpec snapshot — never the live registry.
//  • No redundant derived fields (totalPhaseDuration, qaFirstPass) — derive
//    those in selectors when needed.
//  • No analytics, no scoring, no cross-job rollups in this module.

import type { Job } from "./types";
import type { BoundJobSpec, JobSpecPhaseName } from "./jobSpec";
import { isLegacyBinding } from "./jobSpec";
import type { JobSpecExecution, PhaseExecution, ReconciliationDecision } from "./jobSpecExecution";
import type { MaterialKit, KitOverrideReason } from "./materialKit";
import { isKitFullyConfirmed } from "./materialKit";

export type MaterialKitOutcomeStatus = "fully_confirmed" | "override" | "legacy_no_kit";

export interface SpecExecutionOutcome {
  outcomeVersion: 1;

  jobId: string;
  specId: string;
  specVersion: number;
  isLegacyBinding: boolean;

  contractorId: string;
  payProfileId?: string;

  /** Final phase QA PASS timestamp. Stored — primary sort key for any future query. */
  completedAt: string;

  /** ms from first submit → final QA PASS, per phase. Includes rework loops. */
  phaseDurations: Record<JobSpecPhaseName, number>;
  /** True when a phase reached qa_passed without ever being qa_failed. */
  phaseQaFirstPass: Record<JobSpecPhaseName, boolean>;

  /** Total QA failures across every phase. qaFirstPass (job-level) = (qaFailureCount === 0). */
  qaFailureCount: number;
  /** Count of fail → resubmit cycles per phase (= per-phase qa_failed count). */
  reworkLoopsByPhase: Record<JobSpecPhaseName, number>;

  /** PM reconciliation decision for the demo phase (only gated phase today). */
  reconciliationDecision?: ReconciliationDecision;
  deviationReason?: string;

  materialKitStatus: MaterialKitOutcomeStatus;
  materialOverrideUsed: boolean;
  materialOverrideReason?: KitOverrideReason;

  /**
   * Set if the outcome was retracted (e.g. QA reversal). Never deleted —
   * outcomes are facts, and "this fact was retracted" is itself a fact.
   * Reserved; today the system has no QA reversal flow.
   */
  supersededAt?: string;
}

// ── Pure derivation ───────────────────────────────────────────────────────

export interface DeriveOutcomeArgs {
  job: Pick<Job, "id" | "assignedSubcontractor" | "assignedCrew">;
  bound: BoundJobSpec;
  execution: JobSpecExecution;
  kit?: MaterialKit;
  /** Optional pay profile id for the assigned contractor at completion time. */
  payProfileId?: string;
}

/**
 * Pure, idempotent derivation of a SpecExecutionOutcome.
 *
 * Returns null when the job is not yet terminal — i.e. not every phase is
 * `qa_passed`. Callers should treat null as "not ready, do not write."
 *
 * No side effects: no state writes, no logging, no clock reads outside the
 * inputs themselves. Re-running on the same inputs MUST yield an equivalent
 * record (object equality by value).
 */
export function deriveOutcome(args: DeriveOutcomeArgs): SpecExecutionOutcome | null {
  const { job, bound, execution, kit, payProfileId } = args;

  // Terminal gate — every spec phase must have passed QA. We check against
  // the bound snapshot's phase list (not the execution's phases array) so a
  // truncated execution can never falsely satisfy the predicate.
  const phaseNames: JobSpecPhaseName[] = bound.snapshot
    ? bound.snapshot.phases.map((p) => p.name)
    : execution.phases.map((p) => p.phaseName);

  const phaseByName = new Map<JobSpecPhaseName, PhaseExecution>();
  for (const p of execution.phases) phaseByName.set(p.phaseName, p);

  const allPassed = phaseNames.every((n) => phaseByName.get(n)?.status === "qa_passed");
  if (!allPassed) return null;

  // ── Per-phase metrics ──
  // We don't have a per-attempt timeline in PhaseExecution today; the only
  // signals we have are `completedAt` (most recent submit) and `qaReviewedAt`
  // (most recent review). For v1 we use the current submit→pass interval as
  // the duration. `phaseQaFirstPass` and `reworkLoopsByPhase` rely on whether
  // the phase carries a qaFailReason history — captured here as a boolean
  // (presence of qaFailReason on a now-passed phase means it failed at least
  // once before being resubmitted).
  //
  // This is intentionally conservative for v1. When per-attempt history lands
  // we bump outcomeVersion and recompute.
  const phaseDurations: Record<string, number> = {};
  const phaseQaFirstPass: Record<string, boolean> = {};
  const reworkLoopsByPhase: Record<string, number> = {};
  let qaFailureCount = 0;

  for (const name of phaseNames) {
    const ph = phaseByName.get(name)!;
    const submitTs = ph.completedAt ? Date.parse(ph.completedAt) : NaN;
    const passTs = ph.qaReviewedAt ? Date.parse(ph.qaReviewedAt) : NaN;
    const duration =
      Number.isFinite(submitTs) && Number.isFinite(passTs) && passTs >= submitTs
        ? passTs - submitTs
        : 0;
    phaseDurations[name] = duration;

    // A phase that ever failed QA carries a qaFailReason even after passing
    // (qaPassPhase clears it on success — so its presence on a qa_passed phase
    // shouldn't normally occur; today we treat the field's `lifetime` as a
    // single attempt). We approximate first-pass = no recorded fail reason.
    const everFailed = !!ph.qaFailReason;
    phaseQaFirstPass[name] = !everFailed;
    reworkLoopsByPhase[name] = everFailed ? 1 : 0;
    if (everFailed) qaFailureCount += 1;
  }

  // ── Reconciliation (demo phase today) ──
  let reconciliationDecision: ReconciliationDecision | undefined;
  let deviationReason: string | undefined;
  for (const name of phaseNames) {
    const r = phaseByName.get(name)?.reconciliation;
    if (r) {
      reconciliationDecision = r.decision;
      if (r.decision === "deviation_required" && r.reason) {
        deviationReason = r.reason;
      }
      break; // demo is the only gated phase; first match wins
    }
  }

  // ── Material kit status ──
  let materialKitStatus: MaterialKitOutcomeStatus;
  let materialOverrideUsed = false;
  let materialOverrideReason: KitOverrideReason | undefined;
  if (!kit) {
    materialKitStatus = "legacy_no_kit";
  } else if (kit.override) {
    materialKitStatus = "override";
    materialOverrideUsed = true;
    materialOverrideReason = kit.override.reason;
  } else if (isKitFullyConfirmed(kit)) {
    materialKitStatus = "fully_confirmed";
  } else {
    // Should be unreachable: kit gate prevents install without confirm/override,
    // and terminal QA can't pass without install. Default to override=false /
    // status=fully_confirmed defensively rather than throwing — outcome derivation
    // must never crash during a legitimate state transition.
    materialKitStatus = "fully_confirmed";
  }

  // ── completedAt = max QA pass timestamp across all phases ──
  let completedAtMs = 0;
  for (const name of phaseNames) {
    const ts = phaseByName.get(name)?.qaReviewedAt;
    if (ts) {
      const ms = Date.parse(ts);
      if (Number.isFinite(ms) && ms > completedAtMs) completedAtMs = ms;
    }
  }
  const completedAt = completedAtMs > 0 ? new Date(completedAtMs).toISOString() : new Date(0).toISOString();

  // contractorId resolution: we don't have a stable id on Job today, so use
  // the assigned subcontractor (or crew) name as the stable string. Same
  // pattern other modules use for contractor lookups.
  const contractorId = job.assignedSubcontractor || job.assignedCrew || "";

  return {
    outcomeVersion: 1,
    jobId: job.id,
    specId: bound.specId,
    specVersion: bound.versionNumber,
    isLegacyBinding: isLegacyBinding(bound),

    contractorId,
    payProfileId,

    completedAt,

    phaseDurations: phaseDurations as Record<JobSpecPhaseName, number>,
    phaseQaFirstPass: phaseQaFirstPass as Record<JobSpecPhaseName, boolean>,

    qaFailureCount,
    reworkLoopsByPhase: reworkLoopsByPhase as Record<JobSpecPhaseName, number>,

    reconciliationDecision,
    deviationReason,

    materialKitStatus,
    materialOverrideUsed,
    materialOverrideReason,
  };
}

// ── Selectors (no derived fields stored) ──────────────────────────────────

export function getOutcome(
  outcomes: SpecExecutionOutcome[],
  jobId: string,
): SpecExecutionOutcome | undefined {
  return outcomes.find((o) => o.jobId === jobId);
}

/** Derived: total ms across all phases. Not stored. */
export function totalPhaseDuration(o: SpecExecutionOutcome): number {
  return Object.values(o.phaseDurations).reduce((a, b) => a + b, 0);
}

/** Derived: job-level first-pass = zero QA failures across phases. Not stored. */
export function qaFirstPass(o: SpecExecutionOutcome): boolean {
  return o.qaFailureCount === 0;
}

/** Idempotent upsert helper for callers managing the outcomes array. */
export function upsertOutcome(
  outcomes: SpecExecutionOutcome[],
  next: SpecExecutionOutcome,
): SpecExecutionOutcome[] {
  const idx = outcomes.findIndex((o) => o.jobId === next.jobId);
  if (idx === -1) return [...outcomes, next];
  const copy = outcomes.slice();
  copy[idx] = next;
  return copy;
}
